<section class="payment-details-card cmnh-card simple">
  <div class="card-body">
    <h3 class="payment-heading" id="payment-method-heading">
      {{ "PAYMENT_METHOD_HEADING" | translate }}
    </h3>
    <p class="payment-description">
      {{ "PAYMENT_METHOD_DESCRIPTION" | translate }}
    </p>

    <div class="payment-chooser" role="group" aria-labelledby="payment-method-heading">
      @for (option of choices(); track option.choice) {
      <button type="button" class="payment-choice" [id]="choiceId(option.choice)"
        [class.payment-choice--active]="selected() === option.choice" [attr.aria-pressed]="selected() === option.choice"
        [attr.aria-expanded]="selected() === option.choice" [attr.aria-controls]="panelId(option.choice)"
        (click)="select(option.choice)">
        <span class="payment-choice__badge" aria-hidden="true">
          <app-cmn-icon [name]="CmnIconName.Check" size="0.65rem"></app-cmn-icon>
        </span>
        <span class="payment-choice__icon" aria-hidden="true">
          <app-cmn-icon [name]="option.icon" size="2rem"></app-cmn-icon>
        </span>
        <span class="payment-choice__label">
          {{ (option.labelKey | translate) || option.label }}
        </span>
      </button>
      }
    </div>

    @if (!selected()) {
    <div class="payment-panel payment-panel--empty">
      <app-cmn-icon [name]="CmnIconName.Lock" size="1rem"></app-cmn-icon>
      <span>{{ "PAYMENT_METHOD_PROMPT" | translate }}</span>
    </div>
    }

    <!-- The card panel stays mounted so the Hosted Fields hosts are never destroyed. -->
    <section class="payment-panel" [id]="panelId(PaymentChoice.Card)" role="region"
      [attr.aria-labelledby]="choiceId(PaymentChoice.Card)" tabindex="-1"
      [class.payment-panel--hidden]="selected() !== PaymentChoice.Card">
      <h4 class="payment-panel__title">
        {{ "PAY_WITH_CARD" | translate }}
      </h4>

      <div class="card-field-grid">
        @for (definition of cardFieldDefinitions; track definition.field) {
        <div class="card-field" [class.card-field--full]="definition.full">
          <label class="card-field__label" [attr.for]="definition.hostId">
            {{ definition.labelKey | translate }}
            <span class="required-field">*</span>
          </label>

          <div [id]="definition.hostId" class="card-field__control"
            [class.card-field__control--error]="showCardFieldError(definition.field)"
            [attr.aria-invalid]="showCardFieldError(definition.field)"
            [attr.aria-describedby]="
              showCardFieldError(definition.field) ? cardFieldErrorId(definition.field) : null
            "></div>

          @if (showCardFieldError(definition.field)) {
          <div class="card-field__error" [id]="cardFieldErrorId(definition.field)">
            {{ "THIS_FIELD_IS_REQUIRED" | translate }}
          </div>
          }
        </div>
        }
      </div>

      @if ("PAYMENT_METHOD_NOTICE_OF_COLLECTION" | translate) {
      <a class="braintree-form__notice-of-collection" href="https://www.paypal.com/us/legalhub/home" target="_blank"
        rel="noopener noreferrer">
        {{ "PAYMENT_METHOD_NOTICE_OF_COLLECTION" | translate }}
      </a>
      }
    </section>

    @if (availability().paypal) {
    <section class="payment-panel" [id]="panelId(PaymentChoice.PayPal)" role="region"
      [attr.aria-labelledby]="choiceId(PaymentChoice.PayPal)" tabindex="-1"
      [class.payment-panel--hidden]="selected() !== PaymentChoice.PayPal">
      <h4 class="payment-panel__title">PayPal</h4>

      <div class="wallet-action">
        <div id="paypal-button"></div>
      </div>

      @if (authorized()[PaymentChoice.PayPal]) {
      <p class="wallet-status">
        <app-cmn-icon [name]="CmnIconName.CheckCircle" size="1.1rem"></app-cmn-icon>
        <span>{{ "PAYMENT_METHOD_PAYPAL_AUTHORIZED" | translate }}</span>
      </p>
      }
    </section>
    }

    @if (availability().venmo) {
    <section class="payment-panel" [id]="panelId(PaymentChoice.Venmo)" role="region"
      [attr.aria-labelledby]="choiceId(PaymentChoice.Venmo)" tabindex="-1"
      [class.payment-panel--hidden]="selected() !== PaymentChoice.Venmo">
      <h4 class="payment-panel__title">Venmo</h4>

      <div class="wallet-action">
        <button type="button" class="venmo-button" [disabled]="isAuthorizing()" (click)="authorizeVenmo()">
          <app-cmn-icon [name]="CmnIconName.Venmo" size="1.35rem"></app-cmn-icon>
          <span>{{ "PAYMENT_METHOD_AUTHORIZE_WITH" | translate }} Venmo</span>
        </button>
      </div>

      @if (authorized()[PaymentChoice.Venmo]) {
      <p class="wallet-status">
        <app-cmn-icon [name]="CmnIconName.CheckCircle" size="1.1rem"></app-cmn-icon>
        <span>{{ "PAYMENT_METHOD_VENMO_AUTHORIZED" | translate }}</span>
      </p>
      }
    </section>
    }

    @if (availability().applePay) {
    <section class="payment-panel" [id]="panelId(PaymentChoice.ApplePay)" role="region"
      [attr.aria-labelledby]="choiceId(PaymentChoice.ApplePay)" tabindex="-1"
      [class.payment-panel--hidden]="selected() !== PaymentChoice.ApplePay">
      <h4 class="payment-panel__title">Apple Pay</h4>

      <div class="wallet-action">
        <button type="button" class="apple-pay-button" aria-label="Apple Pay" [disabled]="isAuthorizing()"
          (click)="authorizeApplePay()">
          <span class="apple-pay-button-fallback">
            <app-cmn-icon [name]="CmnIconName.Apple" size="2rem"></app-cmn-icon>
            <span class="apple-pay-button-text">Pay with Apple Pay</span>
          </span>
        </button>
      </div>

      @if (authorized()[PaymentChoice.ApplePay]) {
      <p class="wallet-status">
        <app-cmn-icon [name]="CmnIconName.CheckCircle" size="1.1rem"></app-cmn-icon>
        <span>{{ "PAYMENT_METHOD_APPLE_PAY_AUTHORIZED" | translate }}</span>
      </p>
      }
    </section>
    }

    <section class="payment-panel" [id]="panelId(PaymentChoice.Echeck)" role="region"
      [attr.aria-labelledby]="choiceId(PaymentChoice.Echeck)" tabindex="-1"
      [class.payment-panel--hidden]="!isEcheckSelected()">
      <h4 class="payment-panel__title">{{ "PAY_WITH_ECHECK" | translate }}</h4>

      @if (isEcheckSelected()) {
      <div class="payment-field-grid">
        <app-cmn-input [label]="'ROUTING_NUMBER' | translate" inputId="routingNumber" name="routingNumber" required
          maxlength="9" minlength="9" pattern="[0-9]*(\.?[0-9]{1,2}$)?" #routingnumber="ngModel"
          [(ngModel)]="transaction().abaNumber" [control]="routingnumber" [showError]="showError(routingnumber)"
          [patternMessage]="'VALIDATION_ROUTING_NUMBER' | translate"
          (change)="getBankName()"></app-cmn-input>

        <app-cmn-input [label]="'ACCOUNT' | translate" inputId="bankNumber" name="bankNumber" type="number" required
          pattern="[0-9]*(\.?[0-9]{1,2}$)?" #banknumber="ngModel" [(ngModel)]="transaction().bankAccountNumber"
          [control]="banknumber" [showError]="showError(banknumber)"
          [patternMessage]="'VALIDATION_ACCOUNT_NUMBER' | translate"></app-cmn-input>

        <app-cmn-input [label]="'BANK_NAME' | translate" inputId="bankName" name="bankName" required maxlength="100"
          #bankname="ngModel" [(ngModel)]="transaction().bankName" [control]="bankname"
          [showError]="showError(bankname)"></app-cmn-input>

        <div class="payment-field account-type-field">
          <div class="account-type-toggle">
            <app-cmn-selector type="radio" [checked]="transaction().isCheckingAccount" [label]="'CHECKING' | translate"
              ariaLabel="Select checking account" (toggled)="transaction().isCheckingAccount = true"></app-cmn-selector>
            <app-cmn-selector type="radio" [checked]="!transaction().isCheckingAccount" [label]="'SAVINGS' | translate"
              ariaLabel="Select savings account" (toggled)="transaction().isCheckingAccount = false"></app-cmn-selector>
          </div>
        </div>
      </div>

      <div class="echeck-example-row">
        <img src="assets/images/check99.gif" alt="" class="echeck-example-image" />
      </div>
      }
    </section>
  </div>
</section>
